import { useState, useEffect, useCallback } from "react";
import { useParams, useLocation } from "react-router-dom";
import { useNavigate } from "react-router-dom";
import DashboardHeader from "./HeaderDashboard";
import { getRandomBackground } from "../../utils/imageutils";
import StudyCard from "../ui/cards/studyCard";
import NoStudiesCard from "../ui/cards/NoStudyCard";
import StudyController from "@/network/controllers/study.controller";
import { useSession } from "@/hooks/useSession";
import State, { CurrentStudyState } from "@state";
import InvitationController from "@/network/controllers/invitation.controller";
import Modal from "../ui/Modal";

function InvestigatorDashboard() {
  const navigate = useNavigate();
  const location = useLocation();
  const { id } = useParams();
  const { user } = useSession();

  const [activeTab, setActiveTab] = useState("Assigned studies");
  const [assignedStudies, setAssignedStudies] = useState([]);
  const [invitations, setInvitations] = useState([]);
  const [closedStudies, setClosedStudies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [allStudies, setAllStudies] = useState([]);
  const [isFinalizing, setIsFinalizing] = useState(false);
  const [finalizingStudyId, setFinalizingStudyId] = useState(null);
  const [studyBackgrounds, setStudyBackgrounds] = useState({});

  // Récupère toutes les études pour l'investigateur
  const fetchAllStudies = useCallback(async () => {
    const userIdToFetch = user?.userId;
    if (!userIdToFetch) {
      setLoading(false);
      return;
    }
    try {
      const response = await StudyController.getInvestigatorStudiesByUserId(
        userIdToFetch
      );
      if (response.success) {
        setAllStudies(response.data);
        setStudyBackgrounds((prevStudyBackgrounds) => {
          const newStudyBackgrounds = {};
          response.data.forEach((study) => {
            if (!prevStudyBackgrounds[study.id]) {
              newStudyBackgrounds[study.id] = getRandomBackground();
            } else {
              newStudyBackgrounds[study.id] = prevStudyBackgrounds[study.id];
            }
          });
          return { ...prevStudyBackgrounds, ...newStudyBackgrounds };
        });
        const assigned = response.data.filter(
          (study) => study.invitation_status !== "pending" && study.study_global_status !== "closed"
        );
        const invited = response.data.filter(
          (study) => !study.is_assigned && study.invitation_status === "pending"
        );
        const closed = response.data.filter(
          (study) => study.study_global_status === "closed"
        );
        setInvitations(invited);
        setAssignedStudies(assigned);
        setClosedStudies(closed);
      }
    } catch (error) {
      console.error("Erreur lors de la récupération des études :", error);
    } finally {
      setLoading(false);
    }
  }, [user?.userId]);

  const pollUntilAssigned = async (studyId) => {
    const maxAttempts = 10;
    const intervalMs = 2000;
    let attempts = 0;

    return new Promise((resolve) => {
      const interval = setInterval(async () => {
        attempts++;
        const studies = await StudyController.getInvestigatorStudiesByUserId(
          user.userId
        );
        const found = studies.data.find((s) => s.id === studyId);
        if (found && found.invitation_status !== "pending") {
          clearInterval(interval);
          resolve(true);
        }
        if (attempts >= maxAttempts) {
          clearInterval(interval);
          resolve(false);
        }
      }, intervalMs);
    });
  };

  const handleAcceptInvitation = useCallback(
    async (study) => {
      console.log(
        "=== study === InvestigatorDashboard.jsx === key: 383489 ==="
      );
      console.dir(study, { depth: null, colors: true });
      console.log("=================================");

      const studyId = study?.id;
      const invitationId = study?.invitation_id;
      const siteId = study?.site_id;
      const entityId = study?.entity_id;
      const entityDidAddress = study?.entity_did_address;
      const delegate = study?.delegate || {};

      const token = delegate?.token;

      if (token?.payload) {
        const payloadObject = JSON.parse(token.payload);
        console.log(
          "=== payloadObject === InvestigatorDashboard.jsx === key: 999865 ==="
        );
        console.dir(payloadObject, { depth: null, colors: true });
        console.log("=================================");
      }

      try {
        if (!invitationId) {
          alert("Erreur : ID d'invitation manquant ou invalide.");
          return;
        }

        setIsFinalizing(true);

        const response =
          await InvitationController.acceptInvestigatorInvitation(
            invitationId,
            studyId,
            siteId,
            entityId,
            entityDidAddress,
            delegate
          );
        if (response.success) {
          await pollUntilAssigned(studyId);
          await fetchAllStudies();
        } else {
          alert(
            "Erreur lors de l'acceptation de l'invitation : " + response.message
          );
        }
      } catch (error) {
        alert(
          "Une erreur inattendue s'est produite lors de l'acceptation de l'invitation."
        );
      } finally {
        setIsFinalizing(false);
      }
    },
    [fetchAllStudies]
  ); // Dépend de fetchAllStudies

  const handleDeclineInvitation = useCallback(
    async (study) => {
      const studyId = study?.id;
      const invitationId = study?.invitation_id;
      try {
        console.log(
          "Refus de l'invitation pour l'étude :",
          studyId,
          "ID d'invitation :",
          invitationId
        );
        if (!invitationId) {
          console.error(
            "Erreur : invitationId est indéfini ou nul avant l'appel au contrôleur pour le refus."
          );
          alert("Erreur : ID d'invitation manquant ou invalide pour le refus.");
          return;
        }
        const response =
          await InvitationController.declineInvestigatorInvitation(
            invitationId
          );
        if (response.success) {
          console.log("Invitation refusée avec succès :", response.message);
          await fetchAllStudies();
        } else {
          console.error(
            "Erreur lors du refus de l'invitation :",
            response.message
          );
          alert("Erreur lors du refus de l'invitation : " + response.message);
        }
      } catch (error) {
        console.error(
          "Erreur réseau ou inattendue lors du refus de l'invitation :",
          error
        );
        alert(
          "Une erreur inattendue s'est produite lors du refus de l'invitation."
        );
      }
    },
    [fetchAllStudies]
  ); // Dépend de fetchAllStudies

  // Effet pour la récupération initiale des données
  useEffect(() => {
    fetchAllStudies();
  }, [fetchAllStudies]);

  // Effet pour gérer l'acceptation de l'invitation depuis l'URL APRÈS le chargement des études
  useEffect(() => {
    const queryParams = new URLSearchParams(location.search);
    const invitationIdFromUrl = queryParams.get("invitationId");
    const studyIdFromUrl = queryParams.get("studyId");

    if (
      invitationIdFromUrl &&
      studyIdFromUrl &&
      user?.userId &&
      invitations.length > 0 &&
      !loading
    ) {
      console.log(
        "Tableau de bord : invitationId et studyId trouvés dans l'URL après la connexion."
      );
      // Supprimer les paramètres de requête de l'URL pour éviter de réactiver cette logique
      // lors des rendus ultérieurs ou si l'utilisateur navigue et revient.
      navigate(location.pathname, { replace: true });
      const studyToAccept = invitations.find(
        (study) =>
          study.id === studyIdFromUrl &&
          study.invitation_id === invitationIdFromUrl
      );
      if (studyToAccept) {
        console.log(
          "Tableau de bord : Acceptation de l'invitation pour l'étude :",
          studyToAccept.title
        );
        handleAcceptInvitation(studyToAccept);
      } else {
        console.warn(
          "Tableau de bord : Invitation non trouvée parmi les invitations en attente ou déjà acceptées."
        );
      }
    }
  }, [
    user?.userId,
    location.search,
    invitations,
    navigate,
    loading,
    handleAcceptInvitation,
  ]); // Dépend des invitations et de l'état de chargement

  const handleStudyClick = (studyId, isAssigned) => {
    if (!isAssigned) {
      console.log(
        "Impossible de cliquer sur la carte d'étude tant que l'invitation n'est pas acceptée."
      );
      return;
    }
    const selectedStudy = allStudies.find((study) => study.id === studyId);
    if (selectedStudy) {
      State.setValue(CurrentStudyState.STUDY, selectedStudy);
      console.log(
        "Définition de CurrentStudyState.STUDY depuis InvestigatorDashboard avec :",
        selectedStudy
      );
    }
    navigate(`/investigator/study/${studyId}`);
  };

  const handleTabChange = (tab) => {
    setActiveTab(tab);
  };

  // NOUVELLE LOGIQUE POUR handlePreviewICF
  const handlePreviewICF = async (study) => {
    // Utiliser directement site_icf_document_id de l'objet study
    const protocolId = study?.site_icf_document_id;
    const studyId = study.id;

    if (!protocolId) {
      console.error(
        "Erreur : ID du protocole ICF manquant pour l'aperçu. L'ID n'est pas disponible sur l'objet study.",
        study
      );
      alert("Impossible d'afficher l'ICF : ID du document manquant.");
      return;
    }

    console.log("ID du protocole ICF à afficher:", protocolId);
    console.log("ID de l'étude à afficher:", studyId);

    try {
      // Appeler la fonction pour récupérer et afficher le PDF
      const displayResult = await StudyController.getProtocolDocumentAndDisplay(
        studyId,
        protocolId
      );

      if (!displayResult.success) {
        console.error(
          "Échec de l'affichage du protocole ICF:",
          displayResult.message
        );
        alert(`Impossible d'afficher l'ICF: ${displayResult.message}`);
      }
    } catch (error) {
      console.error("Erreur inattendue lors de l'aperçu de l'ICF:", error);
      alert("Une erreur inattendue s'est produite lors de l'aperçu de l'ICF.");
    }
  };

  const filterButtons = [
    "Assigned studies",
    "Study invitations",
    "Closed studies",
  ];

  const renderAssignedButtons = (study) => (
    <div className="flex justify-center w-full">
      <button
        onClick={(e) => {
          e.stopPropagation();
          handlePreviewICF(study);
        }}
        className="bg-indigo-600 text-white py-1 px-2 sm:py-1.5 sm:px-3 md:py-2 md:px-4 rounded-xl hover:bg-indigo-700 transition-colors text-xs sm:text-sm"
      >
        Preview site ICF
      </button>
    </div>
  );

  const renderInvitationButtons = (study) => (
    <div className="flex flex-col sm:flex-row justify-center space-y-1 sm:space-y-0 sm:space-x-2 w-full">
      <button
        onClick={(e) => {
          e.stopPropagation();
          handlePreviewICF(study); // Passez l'objet study complet
        }}
        className="bg-indigo-600 text-white py-1 px-2 sm:py-1.5 sm:px-3 rounded-xl hover:bg-indigo-700 transition-colors text-xs sm:text-sm"
      >
        Preview ICF
      </button>
      {study?.invitation_id && typeof study.invitation_id === "string" ? (
        <button
          onClick={(e) => {
            e.stopPropagation();
            handleAcceptInvitation(study);
          }}
          className="bg-green-600 text-white py-1 px-2 sm:py-1.5 sm:px-3 rounded-xl hover:bg-green-700 transition-colors text-xs sm:text-sm"
        >
          Accepter
        </button>
      ) : (
        <button
          disabled
          className="bg-gray-400 text-white py-1 px-2 sm:py-1.5 sm:px-3 rounded-xl text-xs sm:text-sm cursor-not-allowed"
        >
          Accepter (ID manquant)
        </button>
      )}
      <button
        onClick={(e) => {
          e.stopPropagation();
          handleDeclineInvitation(study);
        }}
        className="bg-red-600 text-white py-1 px-2 sm:py-1.5 sm:px-3 rounded-xl hover:bg-red-700 transition-colors text-xs sm:text-sm"
      >
        Refuser
      </button>
    </div>
  );

  const getCurrentStudies = () => {
    switch (activeTab) {
      case "Assigned studies":
        return assignedStudies;
      case "Study invitations":
        return invitations;
      case "Closed studies":
      default:
        return closedStudies;
    }
  };

  const currentStudies = getCurrentStudies();
  console.log(
    "Tableau des études actuelles avant le mappage :",
    currentStudies
  );
  const noStudiesInCurrentTab = !loading && currentStudies.length === 0;

  return (
    <div className="flex-1 flex flex-col overflow-hidden p-4 md:p-6 lg:p-8">
      <DashboardHeader
        title="Dashboard"
        buttons={filterButtons}
        activeButton={activeTab}
        onButtonClick={handleTabChange}
      />
      <div className="flex-1 mt-2 rounded-xl overflow-auto bg-dashboard-bg1">
      <main className="p-3 sm:p-4 md:p-6 overflow-y-auto h-[calc(100vh-8rem)]">
          <h2 className="text-lg font-semibold mb-4 md:mb-6">{activeTab}</h2>
          {loading ? (
            <div className="flex justify-center items-center py-10">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600"></div>
            </div>
          ) : (
            <>
              {noStudiesInCurrentTab ? (
                <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-4 2xl:grid-cols-4 gap-4">
                  <div className="w-full aspect-[4/3] sm:aspect-[3/2] md:aspect-square">
                    <NoStudiesCard />
                  </div>
                </div>
              ) : (
                <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-4 2xl:grid-cols-4 gap-4 2xl:gap-6">
                  {currentStudies.map((study) => {
                    console.log(
                      "Objet d'étude dans l'itération de mappage (avant le rendu de la carte) :",
                      study
                    );
                    console.log(
                      "ID de l'étude dans l'itération de mappage :",
                      study?.id,
                      "ID de l'invitation dans l'itération de mappage :",
                      study?.invitation_id
                    );
                    const backgroundImage = studyBackgrounds[study.id] ?? "";
                    const cardVariant =
                      activeTab === "Closed studies"
                        ? "closed"
                        : "investigator";
                    const isClosedStudy = study.study_global_status === "closed";
                    const actionButtonsRenderer =
                      isClosedStudy
                        ? null
                        : activeTab === "Study invitations"
                        ? renderInvitationButtons
                        : activeTab === "Assigned studies"
                        ? renderAssignedButtons
                        : null;
                    const isCardClickable =
                      activeTab === "Closed studies"
                        ? true
                        : (activeTab === "Assigned studies" || study.is_assigned) &&
                          study.study_global_status !== "closed";
                    const studyData = {
                      ...study,
                      name: study.title,
                      createdBy: study.created_by,
                      createdAt: study.created_at,
                      updatedAt: study.last_modified,
                      lastTraceability: study.last_traceability,
                      invitation_id: study.invitation_id,
                    };
                    return (
                      <div
                        key={study.id}
                        className="w-full aspect-[4/3] sm:aspect-[3/2] md:aspect-square"
                      >
                        <StudyCard
                          className="max-w-[340px]"
                          study={studyData}
                          backgroundImage={backgroundImage}
                          onClick={
                            isCardClickable
                              ? () => handleStudyClick(study.id, true)
                              : undefined
                          }
                          variant={cardVariant}
                          actionButtons={actionButtonsRenderer}
                          width="100%"
                        />
                      </div>
                    );
                  })}
                </div>
              )}
            </>
          )}
        </main>
      </div>
      <Modal
        isOpen={isFinalizing}
        onClose={() => {}}
        title="Accepting study invitation..."
        hideCloseButton
        size="md"
      >
        <div className="p-6 flex flex-col items-center gap-4">
          <svg
            className="animate-spin h-8 w-8 text-blue-600"
            xmlns="http://www.w3.org/2000/svg"
            fill="none"
            viewBox="0 0 24 24"
          >
            <circle
              className="opacity-25"
              cx="12"
              cy="12"
              r="10"
              stroke="currentColor"
              strokeWidth="4"
            ></circle>
            <path
              className="opacity-75"
              fill="currentColor"
              d="M4 12a8 8 0 018-8v4a4 4 0 00-4 4H4z"
            ></path>
          </svg>
          <p className="text-center text-gray-700">
            We are currently processing your acceptance for this study.
            <br />
            This may take a few seconds, please do not close this window.
          </p>
        </div>
      </Modal>
    </div>
  );
}

export default InvestigatorDashboard;
